import { useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Send } from "lucide-react";
import { API, useAuth } from "../App";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { CONTACT_EMAIL } from "../config/operator";

// "Email us" — opens a short form; the message lands in THE WALL inbox with Reply-To = sender.
export const EmailUsButton = ({ variant = "light", className = "", testId = "email-us" }) => {
  const { user } = useAuth();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const openIt = () => { setName(user?.name || ""); setEmail(user?.email || user?.recovery_email || ""); setSent(false); setOpen(true); };

  const send = async (e) => {
    e.preventDefault();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) { toast.error("Enter the email you'd like a reply at"); return; }
    if (msg.trim().length < 3) { toast.error("Tell us a little more"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/contact`, { name, email: email.trim(), message: msg, page: loc.pathname }, { withCredentials: true });
      setSent(true); setMsg("");
      toast.success("Sent — we read every message");
    } catch (err) { toast.error(err.response?.data?.detail || "Couldn't send — try again or email us directly"); }
    finally { setBusy(false); }
  };

  const btn = variant === "dark"
    ? "bg-white/10 hover:bg-white/20 text-white border border-white/25"
    : "bg-[#2E5C55] hover:bg-[#234a44] text-white";

  return (
    <>
      <button type="button" onClick={openIt} className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold transition-colors ${btn} ${className}`} data-testid={`${testId}-btn`}>
        <Mail className="h-4 w-4" /> Email us
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid={`${testId}-dialog`}>
          <DialogHeader>
            <DialogTitle className="font-['Playfair_Display'] text-2xl">Email THE WALL</DialogTitle>
            <DialogDescription>Questions, your page, a show, a song, a correction — anything. We reply from a real inbox.</DialogDescription>
          </DialogHeader>
          {sent ? (
            <p className="text-sm text-[#5C5C5C]" data-testid={`${testId}-thanks`}>Got it — thank you. We&rsquo;ll reply to <strong>{email}</strong>. <button type="button" className="underline" onClick={() => setSent(false)}>Send another</button></p>
          ) : (
            <form onSubmit={send} className="space-y-2">
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name (optional)" data-testid={`${testId}-name`} />
              <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Your email — where we reply" data-testid={`${testId}-email`} />
              <Textarea rows={5} required value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Your message…" data-testid={`${testId}-message`} />
              <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
                <a href={`mailto:${CONTACT_EMAIL}`} className="text-xs text-[#5C5C5C] underline" data-testid={`${testId}-mailto`}>or write to {CONTACT_EMAIL}</a>
                <Button type="submit" disabled={busy} className="rounded-full font-bold bg-[#2E5C55] hover:bg-[#234a44] text-white" data-testid={`${testId}-send`}>
                  <Send className="h-4 w-4 mr-1.5" /> {busy ? "Sending…" : "Send"}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
